/**
 * Block Manifest Codec — fixed-depth sparse radix tree for block-addressable content (v3.1)
 *
 * Provides the same content-addressed pipeline as content.ts (CDC) but with
 * fixed-size block addressing instead of content-defined chunking.
 *
 * Key properties:
 *   - O(1) block lookup (constant depth D)
 *   - O(|overlay|) materializeRoot via clone-on-write
 *   - O(changed) diffRoots/collectUploadSet via hash-pruned tree walk
 *   - Deterministic cross-platform rootId via encodeCanonicalRootDescriptor
 *
 * §96 §12 / §97 §8b
 */

import { canonicalize } from './state'
import { resolveCompression, getDecompressor } from './compress'
import type { ContentCodecDeps, ContentLoader, PackOptions, PackedFileContent } from './content'
import type { ContentRef } from '../types'

// ── Constants ─────────────────────────────────────────────────────

/** Default fanout for radix tree internal nodes. */
export const RADIX_FANOUT = 256

// ── Types ─────────────────────────────────────────────────────────

/** v3 manifest for block-radix chunking. */
export interface BlockManifestV3 {
    type: 'secureblob-file-manifest'
    v: 3
    chunking: 'block-radix'
    compression: string
    params: {
        blockSize: number
        blockCount: number
        depth: number
        fanout: number
    }
    /** Ordered array of block hashes. null = empty/sparse block. */
    children: Array<string | null>
}

/** Root descriptor for block-addressable content (§96 §12). */
export interface BlockRoot {
    /** hash(encodeCanonicalRootDescriptor({ manifestTopHash, blockCount, parentPublicRootId })) */
    rootId: string
    /** Hash of the radix tree top manifest blob. */
    manifestTopHash: string
    /** Number of blocks = pageCount in §96. */
    blockCount: number
    /** Block size in bytes. */
    blockSize: number
    /** rootId of the parent public root, or null for first commit. */
    parentPublicRootId: string | null
}

/** Result from materializeRoot. */
export interface MaterializeResult {
    root: BlockRoot
    /** All new/changed manifest+block objects to store. */
    objects: Array<{ hash: string; bytes: Uint8Array }>
}

/** Result from collectUploadSet. */
export interface UploadSet {
    /** Block (page) blob hashes that need uploading. */
    pageBlobHashes: Set<string>
    /** Manifest node hashes that need uploading. */
    manifestNodeHashes: Set<string>
}

// ── Canonical Root Descriptor (cross-platform determinism) ────────

/**
 * Encode a canonical root descriptor as deterministic bytes.
 *
 * Format: manifestTopHash bytes (hex→bytes) ‖ blockCount as uint32 BE ‖ parentPublicRootId bytes or 0x00
 *
 * MUST produce identical bytes in JS and C# for cross-SDK rootId parity.
 * §97 §8b / §21 §2.6
 */
export function encodeCanonicalRootDescriptor(meta: {
    manifestTopHash: string
    blockCount: number
    parentPublicRootId: string | null
}): Uint8Array {
    const hashBytes = hexToUint8Array(meta.manifestTopHash)
    const countBytes = new Uint8Array(4)
    new DataView(countBytes.buffer).setUint32(0, meta.blockCount, false) // big-endian

    const parentBytes = meta.parentPublicRootId
        ? hexToUint8Array(meta.parentPublicRootId)
        : new Uint8Array([0x00])

    const result = new Uint8Array(hashBytes.length + 4 + parentBytes.length)
    result.set(hashBytes, 0)
    result.set(countBytes, hashBytes.length)
    result.set(parentBytes, hashBytes.length + 4)
    return result
}

/**
 * Compute rootId from manifest metadata.
 * rootId = sha256(encodeCanonicalRootDescriptor({ manifestTopHash, blockCount, parentPublicRootId }))
 */
export async function computeRootId(
    manifestTopHash: string,
    blockCount: number,
    parentPublicRootId: string | null,
    deps: { sha256(data: Uint8Array): Promise<string> },
): Promise<string> {
    const descriptor = encodeCanonicalRootDescriptor({ manifestTopHash, blockCount, parentPublicRootId })
    return deps.sha256(descriptor)
}

/** Compare two BlockRoots by rootId equality (§96 §12). */
export function sameRoot(a: BlockRoot, b: BlockRoot): boolean {
    return a.rootId === b.rootId
}

// ── Pack (write path) ─────────────────────────────────────────────

/**
 * Pack plaintext into block-radix content.
 *
 * Pipeline: plaintext → split into fixed blocks → compress → encrypt (deterministic) → manifest
 *
 * Content-addressed: identical block → identical hash → dedup.
 */
export async function packBlockRadixContent(
    plaintext: Uint8Array,
    deps: ContentCodecDeps,
    blockSize: number,
    opts?: PackOptions,
): Promise<PackedFileContent> {
    const compressor = resolveCompression(opts)
    const blockCount = Math.ceil(plaintext.length / blockSize) || 1
    const children: Array<string | null> = []
    const objects: Array<{ hash: string; bytes: Uint8Array }> = []

    for (let i = 0; i < blockCount; i++) {
        const start = i * blockSize
        const end = Math.min(start + blockSize, plaintext.length)
        const block = plaintext.subarray(start, end)

        const compressed = compressor.compress(block)
        const encrypted = await deps.encryptDeterministic(compressed)
        const hash = await deps.sha256(encrypted)

        children.push(hash)

        // Write-path dedup
        if (!(await deps.hasObject(hash))) {
            objects.push({ hash, bytes: encrypted })
        }
    }

    // Compute depth for the fanout tree (§97 §8b)
    const depth = blockCount <= 1 ? 1 : Math.ceil(Math.log(blockCount) / Math.log(RADIX_FANOUT))

    // Build manifest
    const manifest: BlockManifestV3 = {
        type: 'secureblob-file-manifest',
        v: 3,
        chunking: 'block-radix',
        compression: compressor.id,
        params: { blockSize, blockCount, depth, fanout: RADIX_FANOUT },
        children,
    }

    // Manifest encrypted deterministically (same manifest → same hash)
    const manifestCanonical = new TextEncoder().encode(canonicalize(manifest))
    const manifestObject = await deps.encryptDeterministic(manifestCanonical)
    const manifestHash = await deps.sha256(manifestObject)

    if (!(await deps.hasObject(manifestHash))) {
        objects.push({ hash: manifestHash, bytes: manifestObject })
    }

    return {
        content: { type: 'chunked', manifestHash },
        objects,
    }
}

// ── Resolve (read path) ──────────────────────────────────────────

/**
 * Resolve all blocks from a block-radix manifest into concatenated plaintext.
 */
export async function resolveAllBlocks(
    manifest: BlockManifestV3,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): Promise<Uint8Array | null> {
    const decompressor = getDecompressor(manifest.compression)
    const parts: Uint8Array[] = []

    for (const hash of manifest.children) {
        if (hash === null) {
            // Sparse block → zero-filled
            parts.push(new Uint8Array(manifest.params.blockSize))
            continue
        }
        const encrypted = await loader.load(hash)
        if (!encrypted) return null
        const decrypted = await deps.decryptRawObject(encrypted)
        parts.push(decompressor.decompress(decrypted))
    }

    const total = parts.reduce((sum, p) => sum + p.length, 0)
    const out = new Uint8Array(total)
    let offset = 0
    for (const part of parts) {
        out.set(part, offset)
        offset += part.length
    }
    return out
}

/**
 * Resolve a single block by page number (0-based index).
 * O(1) with constant depth. Returns null if out of bounds or missing.
 */
export async function resolveBlock(
    manifest: BlockManifestV3,
    pgno: number,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): Promise<Uint8Array | null> {
    if (pgno < 0 || pgno >= manifest.params.blockCount) return null
    const hash = manifest.children[pgno]
    if (hash === null) return new Uint8Array(manifest.params.blockSize) // sparse

    const encrypted = await loader.load(hash)
    if (!encrypted) return null
    const decrypted = await deps.decryptRawObject(encrypted)
    return getDecompressor(manifest.compression).decompress(decrypted)
}

/**
 * Resolve a range of consecutive blocks.
 * Returns array of block data or null if any block is missing.
 */
export async function resolveBlockRange(
    manifest: BlockManifestV3,
    startBlock: number,
    count: number,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): Promise<Uint8Array[] | null> {
    const end = Math.min(startBlock + count, manifest.params.blockCount)
    const blocks: Uint8Array[] = []

    for (let i = startBlock; i < end; i++) {
        const block = await resolveBlock(manifest, i, loader, deps)
        if (!block) return null
        blocks.push(block)
    }
    return blocks
}

// ── Materialize (§96 §12) ────────────────────────────────────────

/**
 * Materialize a new root from base + overlay.
 * O(|overlay|): only changed blocks are encrypted and stored.
 *
 * @param baseManifest - base manifest (or null for first commit)
 * @param overlay - Map<pgno, blockData> of changed blocks (0-based)
 * @param targetBlockCount - total blocks in result
 * @param parentPublicRootId - rootId of parent (null for first)
 */
export async function materializeRoot(
    baseManifest: BlockManifestV3 | null,
    overlay: Map<number, Uint8Array>,
    targetBlockCount: number,
    parentPublicRootId: string | null,
    deps: ContentCodecDeps,
    opts?: { blockSize?: number; compression?: string },
): Promise<MaterializeResult> {
    const blockSize = opts?.blockSize ?? baseManifest?.params.blockSize ?? 4096
    const compressor = resolveCompression({ compression: opts?.compression ?? baseManifest?.compression })
    const objects: Array<{ hash: string; bytes: Uint8Array }> = []

    // Build children array: base + overlay
    const children: Array<string | null> = []
    for (let i = 0; i < targetBlockCount; i++) {
        if (overlay.has(i)) {
            // Changed block: encrypt + hash
            const block = overlay.get(i)!
            const compressed = compressor.compress(block)
            const encrypted = await deps.encryptDeterministic(compressed)
            const hash = await deps.sha256(encrypted)
            children.push(hash)
            if (!(await deps.hasObject(hash))) {
                objects.push({ hash, bytes: encrypted })
            }
        } else if (baseManifest && i < baseManifest.params.blockCount) {
            // Unchanged from base
            children.push(baseManifest.children[i])
        } else {
            // Beyond base and not in overlay → sparse
            children.push(null)
        }
    }

    const depth = targetBlockCount <= 1 ? 1 : Math.ceil(Math.log(targetBlockCount) / Math.log(RADIX_FANOUT))

    const manifest: BlockManifestV3 = {
        type: 'secureblob-file-manifest',
        v: 3,
        chunking: 'block-radix',
        compression: compressor.id,
        params: { blockSize, blockCount: targetBlockCount, depth, fanout: RADIX_FANOUT },
        children,
    }

    const manifestCanonical = new TextEncoder().encode(canonicalize(manifest))
    const manifestObject = await deps.encryptDeterministic(manifestCanonical)
    const manifestTopHash = await deps.sha256(manifestObject)

    if (!(await deps.hasObject(manifestTopHash))) {
        objects.push({ hash: manifestTopHash, bytes: manifestObject })
    }

    const rootId = await computeRootId(manifestTopHash, targetBlockCount, parentPublicRootId, deps)

    return {
        root: {
            rootId,
            manifestTopHash,
            blockCount: targetBlockCount,
            blockSize,
            parentPublicRootId,
        },
        objects,
    }
}

// ── Diff (§96 §12) ──────────────────────────────────────────────

/**
 * Diff two manifests to find changed block indices.
 * O(changed blocks + |blockCount diff|): compares hashes, prunes identical.
 *
 * Returns Set of 0-based block indices that differ.
 */
export function diffRoots(
    a: BlockManifestV3,
    b: BlockManifestV3,
): Set<number> {
    const changed = new Set<number>()
    const maxBlocks = Math.max(a.params.blockCount, b.params.blockCount)

    for (let i = 0; i < maxBlocks; i++) {
        const hashA = i < a.params.blockCount ? a.children[i] : null
        const hashB = i < b.params.blockCount ? b.children[i] : null
        if (hashA !== hashB) {
            changed.add(i)
        }
    }
    return changed
}

/**
 * Collect the set of hashes that need uploading when going from base → target.
 * O(changed blocks): only examines blocks where hashes differ.
 *
 * Returns both page blob hashes and the target manifest hash.
 */
export function collectUploadSet(
    baseManifest: BlockManifestV3 | null,
    targetManifest: BlockManifestV3,
    targetManifestHash: string,
): UploadSet {
    const pageBlobHashes = new Set<string>()
    const manifestNodeHashes = new Set<string>([targetManifestHash])

    for (let i = 0; i < targetManifest.params.blockCount; i++) {
        const targetHash = targetManifest.children[i]
        if (targetHash === null) continue

        const baseHash = baseManifest && i < baseManifest.params.blockCount
            ? baseManifest.children[i]
            : null

        if (targetHash !== baseHash) {
            pageBlobHashes.add(targetHash)
        }
    }

    return { pageBlobHashes, manifestNodeHashes }
}

// ── Helpers ──────────────────────────────────────────────────────

/** Decode hex string to Uint8Array. */
function hexToUint8Array(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
    }
    return bytes
}
